import axios from 'axios'

const PDF_API_HOST = process.env.REACT_APP_PDF_API_HOST || 'https://pdf-api.tiiny.host'

// Create a separate axios instance for PDF API
const pdfAxios = axios.create({
  baseURL: PDF_API_HOST
})

/**
 * Create a PDF to HTML conversion job
 * @param {File} file - The PDF file to convert
 * @param {string} token - Authentication token
 * @param {string} domain - Optional custom domain
 * @returns {Promise} - Returns job_id
 */
export const createPdfConversionJob = async (file, token, domain = null) => {
  const formData = new FormData()
  formData.append('file', file)
  if (domain) {
    formData.append('domain', domain)
  }

  const response = await pdfAxios.post('/jobs', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`
    }
  })

  return response.data
}

/**
 * Stream PDF conversion job progress using Server-Sent Events
 * @param {string} jobId - The job ID to stream
 * @param {Function} onMessage - Callback for each message
 * @param {Function} onError - Callback for errors
 * @returns {Function} - Cleanup function to close the stream
 */
export const streamPdfConversionJob = (jobId, onMessage, onError) => {
  const MAX_RETRIES = 3
  const BASE_DELAY = 1000
  let retryCount = 0
  let eventSource = null
  let closed = false

  const connect = () => {
    if (closed) return

    eventSource = new EventSource(`${PDF_API_HOST}/jobs/${jobId}/stream`)

    eventSource.onmessage = (event) => {
      retryCount = 0
      try {
        const data = JSON.parse(event.data)
        onMessage(data)
      } catch (error) {
        console.error('Failed to parse SSE message:', error)
      }
    }

    eventSource.onerror = () => {
      eventSource?.close()

      if (closed) return

      if (retryCount < MAX_RETRIES) {
        const delay = BASE_DELAY * Math.pow(2, retryCount)
        retryCount++
        setTimeout(connect, delay)
      } else {
        if (onError) {
          onError(new Error('Connection lost after retries'))
        }
      }
    }
  }

  connect()

  // Return cleanup function
  return () => {
    closed = true
    if (eventSource) {
      eventSource.close()
    }
  }
}

/**
 * Check PDF API health
 * @returns {Promise} - Returns health status and limits
 */
export const checkPdfApiHealth = async () => {
  try {
    const response = await pdfAxios.get('/health')
    return response.data
  } catch (error) {
    console.error('PDF API health check failed:', error)
    return null
  }
}

// Message mapping for user-friendly display
export const PDF_CONVERSION_MESSAGES = {
  'Processing started': 'Starting conversion...',
  'Downloading PDF': 'Preparing your file...',
  'Extracting content': 'Reading your PDF...',
  'Optimizing images': 'Optimizing images...',
  'Requesting LLM generation': 'Doing the magic...',
  'Generating HTML': 'Weaving pixels into code...',
  'Embedding images': 'Polishing up...',
  'Uploading result': 'Uploading...',
  'Conversion complete': 'HTML ready!',
  'Published to Tiiny': 'Published!'
}

export const formatPdfConversionMessage = (message) => {
  return PDF_CONVERSION_MESSAGES[message] || message
}
